/**
 * Uploading into the person's library. Every file goes up in parts through this app into the library's storage (the
 * notes' own part engine: ten MiB parts, six at a time, each retried, a failure picked up from the receipts already
 * earned), then the library checks it. A video or sound is then made playable here, so a room made from it starts
 * without a second encode.
 */
import type { LibraryAsset, UploadPartResult } from "../../shared/types";
import { api, ApiError } from "../lib/api";
import { pollTriesFor } from "../lib/noteUpload";
import { uploadPartsWithRecovery } from "../lib/videoUpload";
import { guessKind } from "./library";

/** Bytes through the Worker are capped; the server says the same. */
const MAX_FILE_BYTES = 50 * 1024 * 1024;

/** The stages a tile shows: 0 Uploading (with %), 1 Checking it, 2 Putting it in the folder, 3 Ready. */
export type UploadStage = 0 | 1 | 2 | 3;

interface UploadProgress {
  stage: UploadStage;
  /** 0 to 100 while uploading. */
  pct: number;
}

export interface Measured {
  width: number | null;
  height: number | null;
  duration_sec: number | null;
}

/** A picture's or video's size and length, read on the device before it goes up. */
export function measure(file: File): Promise<Measured> {
  const none: Measured = { width: null, height: null, duration_sec: null };
  const kind = guessKind(file);
  if (kind !== "image" && kind !== "video") return Promise.resolve(none);
  if (typeof URL.createObjectURL !== "function") return Promise.resolve(none);
  const url = URL.createObjectURL(file);
  const done = (m: Measured) => {
    URL.revokeObjectURL(url);
    return m;
  };
  return new Promise<Measured>((resolve) => {
    const giveUp = setTimeout(() => resolve(done(none)), 4000);
    if (kind === "image") {
      const im = new Image();
      im.addEventListener("load", () => {
        clearTimeout(giveUp);
        resolve(done({ width: im.naturalWidth || null, height: im.naturalHeight || null, duration_sec: null }));
      });
      im.addEventListener("error", () => {
        clearTimeout(giveUp);
        resolve(done(none));
      });
      im.src = url;
      return;
    }
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.addEventListener("loadedmetadata", () => {
      clearTimeout(giveUp);
      resolve(done({ width: v.videoWidth || null, height: v.videoHeight || null, duration_sec: Math.round(v.duration) || null }));
    });
    v.addEventListener("error", () => {
      clearTimeout(giveUp);
      resolve(done(none));
    });
    v.src = url;
  });
}

/** Where each part of a reserved upload goes: this app's own address, which writes it into the library's storage. */
function partUrl(fileId: string, n: number): string {
  return `/api/library/uploads/${encodeURIComponent(fileId)}/parts/${n}`;
}

/**
 * Ask the library to keep the uploaded parts, and ask once more when the answer is lost on the way: the server may
 * have kept the file, and answers the file that is there when asked again (specs/formal/library-upload).
 */
async function finishOnce(fileId: string, parts: UploadPartResult[]): Promise<LibraryAsset> {
  try {
    return await api.library.completeUpload(fileId, { parts });
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) return api.library.completeUpload(fileId, { parts });
    throw e;
  }
}

/** How often a video or sound is asked about while it is made playable. */
const POLL_MS = 2000;

/** Wait for a video or sound's own playable copy: a few minutes, and longer for a heavy file. */
async function untilPlayable(first: LibraryAsset, bytes: number, signal?: AbortSignal): Promise<LibraryAsset> {
  let asset = first;
  for (let i = 0, tries = pollTriesFor(bytes, POLL_MS); !asset.ready && !asset.failed && i < tries; i++) {
    if (signal?.aborted) throw new ApiError(0, "Upload stopped.");
    await new Promise((r) => setTimeout(r, POLL_MS));
    asset = await api.library.one(asset.id);
  }
  if (asset.failed) throw new ApiError(0, "That file could not be made playable. Try again.");
  return asset;
}

/**
 * Put one file in the person's library. `onProgress` walks the tile through its stages; the promise ends with the
 * kept file. A video or sound is then given Flashroom's own playable copy, which the tile waits for ("Checking it").
 */
export async function uploadToLibrary(
  file: File,
  folderId: string | null,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<LibraryAsset> {
  const kind = guessKind(file);
  const media = kind === "video" || kind === "sound";
  if (!media && file.size > MAX_FILE_BYTES) throw new ApiError(0, "That file is too large (50 MB max).");
  const m = await measure(file);
  onProgress({ stage: 0, pct: 0 });
  const start = await api.library.startUpload({
    name: file.name,
    mime: file.type || (kind === "sound" ? "audio/mp4" : kind === "video" ? "video/mp4" : "application/octet-stream"),
    size_bytes: file.size,
    folder_id: folderId,
    width: m.width,
    height: m.height,
    duration_sec: m.duration_sec,
  });
  let receipts: UploadPartResult[];
  try {
    // Why the receipts are handed back and the finish done below: a refusal from the library is an answer, not a
    // part to send again, so it never goes back through the part engine's retries.
    receipts = await uploadPartsWithRecovery(
      file,
      start.part_size,
      {
        sign: async (numbers) => numbers.map((n) => ({ part_number: n, url: partUrl(start.file_id, n) })),
        complete: async (parts) => parts,
      },
      { signal, onPartComplete: (_part, done, total) => onProgress({ stage: 0, pct: (done / total) * 100 }) },
    );
  } catch (e) {
    // Best effort: the reservation is swept after a day anyway; this only frees the place at once.
    void api.library.giveUpUpload(start.file_id).catch(() => {});
    throw e;
  }
  onProgress({ stage: 1, pct: 100 });
  let kept = await finishOnce(start.file_id, receipts);
  kept = media ? await untilPlayable(kept, file.size, signal) : kept;
  onProgress({ stage: 2, pct: 100 });
  onProgress({ stage: 3, pct: 100 });
  return kept;
}
